import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';

/** Project Duck Lua state effects (500-511) — engine mechanics implemented in embedded Lua scripts. */
export const pdLua: Record<number, IBlowDef> = {
    500: {
        name: 'Example Lua State Effect',
        status: 'internal',
        wikiDesc: 'Example Lua state effect.',
        note: 'Template/example effect for developers; not used in real skill data.',
    },
    501: {
        name: 'Mod Bubble Count on Damage Taken',
        wikiDesc: 'Add or remove bubbles upon taking enough damage. Damage accumulates until it ' +
            'reaches the limit, applying the bubble change.',
        describe({ params }) {
            const f = params.numbers();
            const bubbleId = f[0];
            const hpPercent = f[1];
            const bubbleDelta = f[2];
            const bubbleDurationMs = f[3];
            if (isNaN(bubbleId) || isNaN(hpPercent) || isNaN(bubbleDelta) || isNaN(bubbleDurationMs)) {
                return null;
            }
            const verb = bubbleDelta < 0 ? 'removes' : 'gains';
            return [
                'After accumulating ', seg.pct(hpPercent), " of the holder's HP in damage taken, ",
                verb, ' ', seg.num(Math.abs(bubbleDelta)), ' of bubble ', seg.id(bubbleId),
                ' (lasting ', seg.secs(bubbleDurationMs), ')',
            ];
        },
    },
    502: {
        name: 'Apply Buff After Not Moving',
        wikiDesc: 'Applies the given skill as a buff after not moving for waitTime.',
        describe({ params }) {
            const f = params.numbers();
            const waitTimeMs = f[0];
            const skillId = f[1];
            const skillLvl = f[2];
            const buffDurationMs = f[3];
            if (isNaN(waitTimeMs) || isNaN(skillId) || isNaN(skillLvl) || isNaN(buffDurationMs)) {
                return null;
            }
            return [
                'After not moving for ', seg.secs(waitTimeMs), ', applies ', seg.skill(skillId),
                ' as a buff (level ', seg.num(skillLvl), ') for ', seg.secs(buffDurationMs),
            ];
        },
    },
    503: {
        name: 'Apply Buff After Using Skill from Skill Group',
        wikiDesc: 'Applies the given skill as a buff after casting a skill that belongs to the given ' +
            'SkillGroup (not the GCD group).',
        describe({ params }) {
            const f = params.numbers();
            const skillGroup = f[0];
            const skillId = f[1];
            const skillLvl = f[2];
            const buffDurationMs = f[3];
            if (isNaN(skillGroup) || isNaN(skillId) || isNaN(skillLvl) || isNaN(buffDurationMs)) {
                return null;
            }
            return [
                'After casting a skill in skill group ', seg.id(skillGroup), ', applies ',
                seg.skill(skillId), ' as a buff (level ', seg.num(skillLvl), ') for ',
                seg.secs(buffDurationMs),
            ];
        },
    },
    504: {
        name: 'Apply Buff on Timer',
        wikiDesc: 'Applies the given skill as a buff every waitTimeMs.',
        describe({ params }) {
            const f = params.numbers();
            const waitTimeMs = f[0];
            const skillId = f[1];
            const skillLvl = f[2];
            const buffDurationMs = f[3];
            if (isNaN(waitTimeMs) || isNaN(skillId) || isNaN(skillLvl) || isNaN(buffDurationMs)) {
                return null;
            }
            return [
                'Every ', seg.secs(waitTimeMs), ', applies ', seg.skill(skillId), ' as a buff (level ',
                seg.num(skillLvl), ') for ', seg.secs(buffDurationMs),
            ];
        },
    },
    505: {
        name: 'Apply Buff While Under HP Percent',
        wikiDesc: 'Applies the given skill as a buff while under the given HP percentage.',
        describe({ params }) {
            const f = params.numbers();
            const hpPercent = f[0];
            const skillId = f[1];
            const skillLvl = f[2];
            if (isNaN(hpPercent) || isNaN(skillId) || isNaN(skillLvl)) {
                return null;
            }
            return [
                'While under ', seg.pct(hpPercent), ' HP, applies ', seg.skill(skillId),
                ' as a buff (level ', seg.num(skillLvl), ')',
            ];
        },
    },
    506: {
        name: 'Apply Buff While in Actor State',
        wikiDesc: 'Applies the given skill as a buff while in the given actor states (using a state ' +
            'bitmask).',
        describe({ params }) {
            const f = params.numbers();
            const bitmask = f[0];
            const skillId = f[1];
            const skillLvl = f[2];
            if (isNaN(bitmask) || isNaN(skillId) || isNaN(skillLvl)) {
                return null;
            }
            return [
                'While in actor state bitmask ', seg.id(bitmask), ', applies ', seg.skill(skillId),
                ' as a buff (level ', seg.num(skillLvl), ')',
            ];
        },
    },
    507: {
        name: 'Apply Buff Every N Skill Casts',
        wikiDesc: 'Applies the given skill as a buff every Nth skill cast of any skill.',
        describe({ params }) {
            const f = params.numbers();
            const n = f[0];
            const skillId = f[1];
            const skillLvl = f[2];
            const buffDurationMs = f[3];
            if (isNaN(n) || isNaN(skillId) || isNaN(skillLvl) || isNaN(buffDurationMs)) {
                return null;
            }
            return [
                'Every ', seg.num(n), ' skill casts (any skill), applies ', seg.skill(skillId),
                ' as a buff (level ', seg.num(skillLvl), ') for ', seg.secs(buffDurationMs),
            ];
        },
    },
    508: {
        name: 'Increase ATK/MATK per Unique Artifact Acquired',
        wikiDesc: 'Applies Mod% as an ATK/MATK increase.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['Increases ATK/MATK by ', seg.pct(mod), ' per unique artifact acquired'];
        },
    },
    509: {
        name: 'Increase ATK/MATK per Listed Skill',
        wikiDesc: "Increase ATK/MATK per skill listed in blow509_de_gainatkpernegativeartifact.lua " +
            "line 64. Put in the skill IDs that qualify for 'negative' artifacts.",
        describe({ params }) {
            const f = params.numbers();
            const n = f[0];
            const skillId = f[1];
            const skillLvl = f[2];
            const buffDurationMs = f[3];
            if (isNaN(n) || isNaN(skillId) || isNaN(skillLvl) || isNaN(buffDurationMs)) {
                return null;
            }
            return [
                'Every ', seg.num(n), ' skill casts (any skill), applies ', seg.skill(skillId),
                ' as a buff (level ', seg.num(skillLvl), ') for ', seg.secs(buffDurationMs),
            ];
        },
    },
    510: {
        name: 'Apply Skill If All Other Skill Buffs Are Active',
        wikiDesc: 'Used for the Solace of Oblivion Avatar artifact. Checks every 10 seconds.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const checkIds = ParamReader.numList(f[0], ',');
            const buffSkillId = ParamReader.num(f[1]);
            if (!checkIds.length || isNaN(buffSkillId)) {
                return null;
            }
            return [
                'Every 10 seconds, if all of ',
                seg.list(checkIds.map((id) => [seg.skill(id)] as Segment[])),
                "'s buffs are active, applies ", seg.skill(buffSkillId),
            ];
        },
    },
    511: {
        name: 'Increase Damage Dealt of Next Cast',
        wikiDesc: 'Applies Mod% as a damage coefficient where 1.0 is normal damage. SkillType: 0 = any, ' +
            '1 = active only, 2 = passive only. If a skill ID list is specified, only those skills can ' +
            'proc this.',
        describe({ params }) {
            const f = params.fields();
            const mod = ParamReader.num(f[0]);
            if (isNaN(mod)) {
                return null;
            }
            const out: Segment[] = ['Next cast deals ', seg.pct(mod), ' damage'];
            const SKILL_TYPES = ['any skill', 'active skills only', 'passive skills only'];
            let skillListField: string | undefined;
            if (f.length > 1 && f[1] !== '') {
                const skillType = ParamReader.num(f[1]);
                if (!isNaN(skillType) && f[1].indexOf(',') === -1) {
                    out.push(' (', seg.en(skillType, SKILL_TYPES, 'unknown'), ')');
                    skillListField = f[2];
                } else {
                    skillListField = f[1];
                }
            }
            const skillIds = skillListField ? ParamReader.numList(skillListField, ',') : [];
            if (skillIds.length) {
                out.push(', restricted to ',
                    seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])));
            }
            return out;
        },
    },
};
